import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { QRCodeCanvas } from 'qrcode.react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const steps = ['Basic Information', 'Weight Measurements', 'Review & Submit'];

function ShipmentForm() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', severity: 'info' });

  const [formData, setFormData] = useState({
    "ID Number": '',
    "Collection Point": '',
    "Specific Food Item": '',
    "Wet Weight": '',
    "Dry Weight": '',
    "Date": new Date().toISOString().split('T')[0],
    "Notes": '',
    "Next Step": "Pending",
    qrCodeValue: '',
  });

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const qrValue = JSON.stringify({
        id: formData["ID Number"],
        collectionPoint: formData["Collection Point"],
        foodItem: formData["Specific Food Item"],
        date: formData["Date"],
      });

      const submissionData = {
        ...formData,
        qrCodeValue: qrValue,
      };

      await addDoc(collection(db, 'shipments'), submissionData);
      
      setMessage({
        text: 'Shipment created successfully!',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/shipments');
      }, 2000);
    } catch (error) {
      console.error('Error creating shipment:', error);
      setMessage({
        text: 'Error creating shipment. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="ID Number"
                    name="ID Number"
                    value={formData["ID Number"]}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Collection Point"
                    name="Collection Point"
                    value={formData["Collection Point"]}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Specific Food Item"
                    name="Specific Food Item"
                    value={formData["Specific Food Item"]}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    type="date"
                    label="Date"
                    name="Date"
                    value={formData["Date"]}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      case 1:
        return (
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label="Wet Weight (kg)"
                    name="Wet Weight"
                    value={formData["Wet Weight"]}
                    onChange={handleChange}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    type="number"
                    label="Dry Weight (kg)"
                    name="Dry Weight"
                    value={formData["Dry Weight"]}
                    onChange={handleChange}
                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Notes"
                    name="Notes"
                    value={formData["Notes"]}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      case 2:
        return (
          <Card>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Review Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        ID Number
                      </Typography>
                      <Typography variant="body1">{formData["ID Number"]}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Collection Point
                      </Typography>
                      <Typography variant="body1">{formData["Collection Point"]}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Food Item
                      </Typography>
                      <Typography variant="body1">{formData["Specific Food Item"]}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Date
                      </Typography>
                      <Typography variant="body1">{formData["Date"]}</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Wet Weight
                      </Typography>
                      <Typography variant="body1">{formData["Wet Weight"]} kg</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Dry Weight
                      </Typography>
                      <Typography variant="body1">{formData["Dry Weight"]} kg</Typography>
                    </Grid>
                    {formData["Notes"] && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Notes
                        </Typography>
                        <Typography variant="body1">{formData["Notes"]}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <QRCodeCanvas
                      value={JSON.stringify({
                        id: formData["ID Number"],
                        collectionPoint: formData["Collection Point"],
                        foodItem: formData["Specific Food Item"],
                        date: formData["Date"],
                      })}
                      size={200}
                    />
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          onClick={() => navigate('/shipments/new')}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          Back to Form Selection
        </Button>
        <Typography variant="h4" component="h1">
          New Standard Shipment
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {message.text && (
        <Alert severity={message.severity} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      {renderStepContent(activeStep)}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        {activeStep > 0 && (
          <Button onClick={handleBack} sx={{ mr: 1 }}>
            Back
          </Button>
        )}
        {activeStep === steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default ShipmentForm; 